import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/admin/ImageUploader";
import type { LandingConfig, LandingButton } from "@/lib/landingConfig";

interface Props {
  value: LandingConfig;
  onChange: (next: LandingConfig) => void;
}

const ACTION_OPTIONS: { value: NonNullable<LandingButton["action"]>; label: string }[] = [
  { value: "whatsapp", label: "Abrir WhatsApp" },
  { value: "scroll_cta", label: "Rolar até o bloco CTA" },
  { value: "url", label: "Abrir URL externa" },
];

const LandingConfigForm = ({ value, onChange }: Props) => {
  const hero = value.hero || {};
  const cta = value.cta || {};

  const setHero = (patch: Partial<LandingConfig["hero"]>) =>
    onChange({ ...value, hero: { ...hero, ...patch } });
  const setCta = (patch: Partial<LandingConfig["cta"]>) =>
    onChange({ ...value, cta: { ...cta, ...patch } });
  const setPrimary = (patch: Partial<LandingButton>) =>
    setHero({ primary_button: { ...(hero.primary_button || {}), ...patch } });
  const setSecondary = (patch: Partial<LandingButton>) =>
    setHero({ secondary_button: { ...(hero.secondary_button || {}), ...patch } });
  const setCtaButton = (patch: Partial<LandingButton>) =>
    setCta({ button: { ...(cta.button || {}), ...patch } });

  const single = (v?: string) => (v ? [v] : []);

  return (
    <div className="border-t border-border pt-4 mt-4 space-y-6">
      <div>
        <h3 className="font-display text-base font-bold text-foreground mb-1">Landing Page (opcional)</h3>
        <p className="text-xs text-muted-foreground font-body">
          Preencha para gerar uma landing personalizada. Deixe vazio para usar o layout padrão.
        </p>
      </div>

      {/* ── HERO ── */}
      <details className="border border-border rounded-md">
        <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
          🎯 Hero personalizado
        </summary>
        <div className="p-3 space-y-3">
          <div>
            <label className="text-sm font-medium text-foreground font-body block mb-1">Imagem Hero Desktop</label>
            <ImageUploader images={single(hero.image_desktop)} onChange={(imgs) => setHero({ image_desktop: imgs[0] || "" })} />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground font-body block mb-1">Imagem Hero Mobile</label>
            <ImageUploader images={single(hero.image_mobile)} onChange={(imgs) => setHero({ image_mobile: imgs[0] || "" })} />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground font-body block mb-1">Imagem de fundo (opcional)</label>
            <ImageUploader images={single(hero.background_image)} onChange={(imgs) => setHero({ background_image: imgs[0] || "" })} />
          </div>
          <Input
            placeholder="Vídeo do Hero (URL YouTube/Vimeo)"
            value={hero.video_url || ""}
            onChange={(e) => setHero({ video_url: e.target.value })}
            className="font-body"
          />
          <Input
            placeholder='Texto do selo (ex.: "Exclusividade TopClass")'
            value={hero.badge_text || ""}
            onChange={(e) => setHero({ badge_text: e.target.value })}
            maxLength={60}
            className="font-body"
          />
          <Textarea
            placeholder="Texto de apoio (parágrafo curto acima dos botões)"
            value={hero.support_text || ""}
            onChange={(e) => setHero({ support_text: e.target.value })}
            rows={2}
            maxLength={400}
            className="font-body"
          />
          <Input
            placeholder='Texto acima do botão (ex.: "Agende uma visita exclusiva")'
            value={hero.above_button_text || ""}
            onChange={(e) => setHero({ above_button_text: e.target.value })}
            maxLength={80}
            className="font-body"
          />

          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
            <p className="text-xs font-semibold text-foreground font-body">Botão principal</p>
            <Input
              placeholder="Rótulo do botão"
              value={hero.primary_button?.label || ""}
              onChange={(e) => setPrimary({ label: e.target.value })}
              className="font-body"
            />
            <Select
              value={hero.primary_button?.action || "whatsapp"}
              onValueChange={(v) => setPrimary({ action: v as any })}
            >
              <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {hero.primary_button?.action === "url" && (
              <Input
                placeholder="URL de destino"
                value={hero.primary_button?.url || ""}
                onChange={(e) => setPrimary({ url: e.target.value })}
                className="font-body"
              />
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-body">Cor do botão</label>
              <input
                type="color"
                value={hero.primary_button?.color || "#c9a24a"}
                onChange={(e) => setPrimary({ color: e.target.value })}
                className="h-9 w-16 rounded border border-border cursor-pointer"
              />
              {hero.primary_button?.color && (
                <button
                  type="button"
                  onClick={() => setPrimary({ color: "" })}
                  className="text-xs text-muted-foreground hover:text-foreground font-body underline"
                >
                  Usar padrão
                </button>
              )}
            </div>
          </div>

          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
            <p className="text-xs font-semibold text-foreground font-body">Botão secundário (opcional)</p>
            <Input
              placeholder="Rótulo do botão"
              value={hero.secondary_button?.label || ""}
              onChange={(e) => setSecondary({ label: e.target.value })}
              className="font-body"
            />
            <Select
              value={hero.secondary_button?.action || "scroll_cta"}
              onValueChange={(v) => setSecondary({ action: v as any })}
            >
              <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {hero.secondary_button?.action === "url" && (
              <Input
                placeholder="URL de destino"
                value={hero.secondary_button?.url || ""}
                onChange={(e) => setSecondary({ url: e.target.value })}
                className="font-body"
              />
            )}
          </div>
        </div>
      </details>

      {/* ── CTA ── */}
      <details className="border border-border rounded-md">
        <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
          📣 Bloco CTA
        </summary>
        <div className="p-3 space-y-3">
          <Input
            placeholder="Título do CTA"
            value={cta.title || ""}
            onChange={(e) => setCta({ title: e.target.value })}
            maxLength={120}
            className="font-body"
          />
          <Input
            placeholder="Subtítulo do CTA"
            value={cta.subtitle || ""}
            onChange={(e) => setCta({ subtitle: e.target.value })}
            maxLength={160}
            className="font-body"
          />
          <Textarea
            placeholder="Texto de apoio"
            value={cta.support_text || ""}
            onChange={(e) => setCta({ support_text: e.target.value })}
            rows={2}
            maxLength={400}
            className="font-body"
          />
          <div>
            <label className="text-sm font-medium text-foreground font-body block mb-1">Imagem / ilustração</label>
            <ImageUploader images={single(cta.image)} onChange={(imgs) => setCta({ image: imgs[0] || "" })} />
          </div>
          <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
            <p className="text-xs font-semibold text-foreground font-body">Botão do CTA</p>
            <Input
              placeholder="Rótulo do botão"
              value={cta.button?.label || ""}
              onChange={(e) => setCtaButton({ label: e.target.value })}
              className="font-body"
            />
            <Select
              value={cta.button?.action || "whatsapp"}
              onValueChange={(v) => setCtaButton({ action: v as any })}
            >
              <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">Abrir WhatsApp</SelectItem>
                <SelectItem value="url">Abrir URL externa</SelectItem>
              </SelectContent>
            </Select>
            {cta.button?.action === "url" && (
              <Input
                placeholder="URL de destino"
                value={cta.button?.url || ""}
                onChange={(e) => setCtaButton({ url: e.target.value })}
                className="font-body"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground font-body">Cor de fundo</label>
            <input
              type="color"
              value={cta.background_color || "#0b1f3a"}
              onChange={(e) => setCta({ background_color: e.target.value })}
              className="h-9 w-16 rounded border border-border cursor-pointer"
            />
            {cta.background_color && (
              <button
                type="button"
                onClick={() => setCta({ background_color: "" })}
                className="text-xs text-muted-foreground hover:text-foreground font-body underline"
              >
                Usar padrão
              </button>
            )}
          </div>

          <Textarea
            placeholder="Mensagem WhatsApp pré-preenchida (opcional). Ex.: Olá, tenho interesse neste imóvel..."
            value={cta.whatsapp_message || ""}
            onChange={(e) => setCta({ whatsapp_message: e.target.value })}
            rows={2}
            maxLength={500}
            className="font-body"
          />

          <div className="grid grid-cols-2 gap-2 pt-1">
            <label className="flex items-center gap-2 text-xs font-body text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={!!cta.show_form}
                onChange={(e) => setCta({ show_form: e.target.checked })}
              />
              Mostrar formulário
            </label>
            <label className="flex items-center gap-2 text-xs font-body text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={!!cta.show_phone}
                onChange={(e) => setCta({ show_phone: e.target.checked })}
              />
              Mostrar telefone
            </label>
            <label className="flex items-center gap-2 text-xs font-body text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={!!cta.show_call_button}
                onChange={(e) => setCta({ show_call_button: e.target.checked })}
              />
              Mostrar botão "Ligar"
            </label>
            <label className="flex items-center gap-2 text-xs font-body text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={!!cta.show_tour_button}
                onChange={(e) => setCta({ show_tour_button: e.target.checked })}
              />
              Mostrar Tour Virtual
            </label>
          </div>

          {cta.show_tour_button && (
            <Input
              placeholder="URL do Tour Virtual (ex.: Matterport, YouTube)"
              value={cta.tour_url || ""}
              onChange={(e) => setCta({ tour_url: e.target.value })}
              className="font-body"
            />
          )}
        </div>
      </details>

    </div>
  );
};

export default LandingConfigForm;
